import { CHILD_INDENT_PX } from "@/components/rail-kit/registers";
import { cn } from "@/utils";
import { Children, useEffect, useState } from "react";

/**
 * RailReveal — the one disclosure animation for lazily-filled content.
 *
 * Expanding a rail section used to be two jumps: the subtree appeared at
 * full height in a single frame, and then jumped AGAIN when its query
 * resolved and rows replaced the empty space. This primitive removes both
 * without preloading anything:
 *
 * 1. **The reveal animates.** A `0fr → 1fr` grid row animates to the
 *    content's natural height — no measuring, no max-height guess, and the
 *    house deceleration curve throughout.
 * 2. **The height is reserved before the data lands.** While the query is
 *    pending we render placeholder rows at the row pitch, so the reveal
 *    animates ONCE, to roughly the final height, and the real rows land in
 *    space already made for them. (Persisted queries make a pending expand
 *    rare — this is the fallback for genuinely cold or invalidated data.)
 * 3. **Fast data never flashes a skeleton.** For the first ~120ms the
 *    reserved space is simply blank; the pulsing bars only fade in if the
 *    fetch is genuinely slow. A warm cache (staleTime keeps most re-expands
 *    warm) therefore shows content with no loading state at all.
 *
 * Children stay mounted for the collapse animation and unmount once it
 * finishes, so a closed section costs nothing and its query can go idle.
 */

/** The disclosure duration — the shell's standard structural move. */
const REVEAL_MS = 200;
/** How long a fetch may take before we admit to loading. */
const SKELETON_DELAY_MS = 120;
/**
 * Before a surface has ever been opened we know nothing about its contents,
 * so we invent no height: guessing HIGH makes content shrink when the truth
 * arrives, which reads as a glitch, while guessing low simply grows — the
 * ordinary shape of loading. Callers whose surface is reliably populated
 * (a workspace's Canvases, its Library) pass `fallbackRows` to buy a
 * smoother first open; surfaces that are often empty (an org with no
 * integrations) leave it at zero.
 */
const DEFAULT_RESERVED_ROWS = 0;
/**
 * Reserving past a screenful buys nothing — the rail's list scrolls, so rows
 * below the fold are not visible to settle in the first place — and it would
 * put a pile of throwaway nodes in the DOM.
 */
const MAX_RESERVED_ROWS = 24;

/**
 * The reservation policy, shared by every surface that reveals lazily-loaded
 * rows — rail sections and the canvas menus alike. Returns how many
 * placeholder rows to hold open, and whether the placeholders should be
 * *visible* yet (fast fetches never flash a skeleton).
 */
export function useRowReservation({
  active,
  pending,
  fallbackRows = DEFAULT_RESERVED_ROWS,
}: {
  /** The surface is open/mounted — reservations only apply while visible. */
  active: boolean;
  pending: boolean;
  /** Rows to hold open while the list is genuinely cold. */
  fallbackRows?: number;
}): { reservedRows: number; skeletonVisible: boolean } {
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  useEffect(() => {
    if (!active || !pending) {
      setSkeletonVisible(false);
      return;
    }
    const timer = window.setTimeout(
      () => setSkeletonVisible(true),
      SKELETON_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [active, pending]);

  const reservedRows = Math.min(MAX_RESERVED_ROWS, Math.max(0, fallbackRows));
  return { reservedRows, skeletonVisible };
}

/**
 * A row's worth of held-open space. Invisible by default — it exists to
 * occupy height — and only pulses once `visible` says the wait is long
 * enough to acknowledge. `menu` matches the 32px item rung the canvas menus
 * use; the default matches the rail's 28px row.
 */
export function RailRowSkeleton({
  visible,
  size = "rail",
}: {
  visible: boolean;
  size?: "rail" | "menu";
}) {
  const bar = cn(
    "rounded-sm bg-n-10 transition-opacity duration-150 ease-out",
    visible ? "animate-pulse opacity-100" : "opacity-0",
  );
  const menu = size === "menu";
  return (
    <div
      className={cn(
        "flex items-center",
        menu ? "min-h-8 gap-2 px-2 py-1.5" : "min-h-7 gap-2.5 px-2",
      )}
      aria-hidden
    >
      <div className={cn(bar, "shrink-0", menu ? "size-4" : "size-3.5")} />
      <div className={cn(bar, "h-2", menu ? "w-24" : "flex-1")} />
    </div>
  );
}

export function RailReveal({
  open,
  pending = false,
  fallbackRows,
  indent = false,
  contentClassName,
  className,
  children,
}: {
  open: boolean;
  /** The revealed content's query is still resolving. */
  pending?: boolean;
  /** Rows to hold open the first time this section is opened (see the hook). */
  fallbackRows?: number;
  /** Apply the kit's per-level child indent (12px half-inset). */
  indent?: boolean;
  /** Layout for the revealed stack. Defaults to the kit's gapped column;
   *  tree callers pass their own indent step (e.g. "flex flex-col pl-2"). */
  contentClassName?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  // Keep the subtree mounted across the collapse so it animates shut, then
  // drop it so closed sections cost nothing.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setMounted(false), REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const childCount = Children.count(children);
  const { reservedRows, skeletonVisible } = useRowReservation({
    active: open,
    pending,
    fallbackRows,
  });

  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        className,
      )}
      style={{ transitionDuration: `${REVEAL_MS}ms` }}
    >
      <div className="min-h-0 overflow-hidden">
        {mounted && (
          <div
            className={contentClassName ?? "flex flex-col gap-0.5"}
            style={indent ? { paddingLeft: CHILD_INDENT_PX } : undefined}
          >
            {pending && childCount === 0
              ? Array.from({ length: reservedRows }, (_, i) => (
                  <RailRowSkeleton key={i} visible={skeletonVisible} />
                ))
              : children}
          </div>
        )}
      </div>
    </div>
  );
}
